import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { countryLocale, fetchCountry, resolveLocaleSync } from './detect'
import { mergeCatalogues, DESIGN_CATALOGUE_EN, DESIGN_LOADERS } from './catalogue'
import { DEFAULT_LOCALE, LOCALE_INFO, type Catalogue, type Locale } from './types'

const STORAGE_KEY = 'kkf.locale'

export interface LocaleContextValue {
  locale: Locale
  /**
   * The BCP 47 tag. **Use this for every `Intl.*` call, never `locale`.** See `LocaleInfo`
   * for the failure a short code produces — a silent one.
   */
  intlLocale: string
  dir: 'ltr' | 'rtl'
  setLocale: (locale: Locale) => void
  /** Look a key up in the current catalogue, falling back to English, then to the key. */
  t: (key: string, fallback?: string) => string
  /**
   * Whether the current locale's catalogue actually covers the app.
   *
   * False means the reader asked for a language we do not have yet and is looking at
   * English — which the app must say out loud rather than let them assume the page simply
   * has little on it. `LocaleNotice` is that sentence.
   */
  translated: boolean
  /** How much of the English catalogue this locale covers, 0–1. For the notice, and for us. */
  coverage: number
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export interface LocaleProviderProps {
  /**
   * The app's English catalogue, statically imported — it is the fallback for every key in
   * every language, so it has to be in the initial bundle.
   */
  english: Catalogue
  /**
   * The other locales, as loaders. `import.meta.glob` in the app produces exactly this
   * shape, and Vite turns each into its own chunk — so a first visit downloads one language.
   *
   * Keyed by locale. A locale with no loader falls back to English, which is the honest
   * state of a language nobody has translated yet.
   */
  loaders?: Partial<Record<Locale, () => Promise<{ default: Catalogue }>>>
  children: ReactNode
  /** Skip the country step. For tests, and for a host that is not behind Cloudflare. */
  detectCountry?: boolean
}

/**
 * The platform's language state, for every app.
 *
 * It lives in `app_design` because all five apps need the same one and ground rule 5 gives
 * a shared block one definition — and because the *rules* are the shared part. What varies
 * is the strings, and those arrive as `catalogues` exactly the way nav entries and footer
 * copy arrive as props.
 *
 * ## What it does on mount, in order
 *
 * The first render is synchronous and uses what can be known for free: a stored choice, then
 * the browser's languages, then English (`resolveLocaleSync`). Only if the browser said
 * nothing we speak does it then ask Cloudflare which country the request came from, and that
 * happens **after** paint — a page that waits on the network to pick a language renders
 * nothing while it waits.
 *
 * A stored choice ends the matter. It is the one signal that is a statement rather than an
 * inference, so neither step below it may override it.
 *
 * ## It sets `<html lang>`
 *
 * Not cosmetic: it is what tells a screen reader which pronunciation rules to use, and
 * getting it wrong makes English read aloud in Mongolian phonology or the reverse.
 */
export function LocaleProvider({
  english,
  loaders,
  children,
  detectCountry = true,
}: LocaleProviderProps) {
  /** English, always resolved, always in the bundle. Design-system strings then the app's. */
  const base = useMemo(() => mergeCatalogues(DESIGN_CATALOGUE_EN, english), [english])
  /** The active locale's catalogue, once it has arrived. English needs no loading. */
  const [loaded, setLoaded] = useState<Catalogue | null>(null)

  const [state, setState] = useState(() =>
    resolveLocaleSync({
      storage: typeof window === 'undefined' ? undefined : window.localStorage,
      storageKey: STORAGE_KEY,
      languages: typeof navigator === 'undefined' ? undefined : navigator.languages,
    }),
  )

  // Step 3, after paint, and only when the browser gave us nothing to go on.
  useEffect(() => {
    if (!detectCountry || !state.needsCountry) return
    let live = true
    void fetchCountry().then((country) => {
      const fromCountry = countryLocale(country)
      if (!live || !fromCountry) return
      // Still not a *choice* — a later explicit pick must be able to override it.
      setState({ locale: fromCountry, chosen: false, needsCountry: false })
    })
    return () => {
      live = false
    }
  }, [detectCountry, state.needsCountry])

  /**
   * Fetch the locale's chunks when it changes — **the design system's and the app's.**
   *
   * English is already here, so it short-circuits. A locale with no loader on either side
   * resolves to nothing and `t` falls through to English per key.
   *
   * Both are fetched because both are in `base`: `mergeCatalogues` puts the `ul.*` strings
   * under the app's, and `coverage` is measured over the result. Loading only the app's —
   * which is what this did until 2026-09-18 — left the seven shared strings English inside
   * a translated page and held `coverage` permanently below 1, so `LocaleNotice` announced
   * an unfinished translation to a reader looking at a finished one.
   *
   * `Promise.all` rather than two effects: one `setLoaded` means the page changes language
   * once, instead of flickering through a state where the chrome and the copy disagree.
   * The app's entries go last, so an app may override a `ul.*` string in its own language
   * exactly as it may in English.
   */
  useEffect(() => {
    if (state.locale === DEFAULT_LOCALE) {
      setLoaded(null)
      return
    }
    const design = DESIGN_LOADERS[state.locale]
    const app = loaders?.[state.locale]
    if (!design && !app) {
      setLoaded(null)
      return
    }
    let live = true
    // A chunk that will not load is one language file missing, not a failed page: settle
    // each independently so a broken app catalogue still gets the shared strings, and the
    // reverse. English is already rendered underneath either way.
    const safely = (load?: () => Promise<{ default: Catalogue }>) =>
      load ? load().then((mod) => mod.default).catch(() => ({}) as Catalogue) : Promise.resolve({} as Catalogue)

    void Promise.all([safely(design), safely(app)]).then(([designCatalogue, appCatalogue]) => {
      if (live) setLoaded(mergeCatalogues(designCatalogue, appCatalogue))
    })
    return () => {
      live = false
    }
  }, [state.locale, loaders])

  useEffect(() => {
    const info = LOCALE_INFO[state.locale]
    // `lang` is what tells a screen reader which pronunciation rules to use; getting it
    // wrong reads English aloud in Mongolian phonology or the reverse. `dir` is set
    // unconditionally so a right-to-left language later needs no new code here.
    document.documentElement.lang = info.intlTag
    document.documentElement.dir = info.dir
  }, [state.locale])

  const setLocale = useCallback((locale: Locale) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      // Storage blocked. The choice still applies for this visit, which is the most that
      // can honestly be offered — the same trade `useFavourites` makes.
    }
    setState({ locale, chosen: true, needsCountry: false })
  }, [])

  const value = useMemo<LocaleContextValue>(() => {
    const active = loaded ?? {}
    const total = Object.keys(base).length
    const covered = Object.keys(base).filter((key) => active[key] !== undefined).length
    const info = LOCALE_INFO[state.locale]

    return {
      locale: state.locale,
      intlLocale: info.intlTag,
      dir: info.dir,
      setLocale,
      // English, then the key itself. Returning the key is deliberate: a missing string
      // shows up as `board.empty` on the page, which somebody notices, rather than as an
      // empty element, which nobody does.
      t: (key, fallback) => active[key] ?? base[key] ?? fallback ?? key,
      translated: state.locale === DEFAULT_LOCALE || (total > 0 && covered === total),
      coverage: total === 0 ? 1 : covered / total,
    }
  }, [base, loaded, state.locale, setLocale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

/**
 * The language state.
 *
 * Throws outside a provider rather than falling back to English, because a silent fallback
 * is a page that looks translated and is not — and the failure would only show up in the
 * one language nobody on the team reads.
 */
export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext)
  if (!value) throw new Error('useLocale() needs a <LocaleProvider> above it')
  return value
}

/** Just the lookup, which is what most components want. */
export function useT(): LocaleContextValue['t'] {
  return useLocale().t
}

/**
 * The lookup, or English, for a component that cannot insist on a provider.
 *
 * ## What this is for, and what it is not
 *
 * `useLocale` throws outside a provider on purpose, and that is right for an **app**: a
 * silent fallback there is a page that looks translated and is not, in the one language
 * nobody on the team reads.
 *
 * A shared component is the other case. `DirectoryFilters` is rendered by the garage and
 * the resort, `BlogCard` by the Foundation, and a component that throws unless somebody
 * remembered a provider is a component that cannot be dropped into a page. `Combobox`'s
 * own header records the consequence: its words became required props precisely because it
 * could not look them up.
 *
 * So this resolves against the design system's English catalogue when there is no provider
 * above it, which is **exactly what the hard-coded default it replaces did** — the same
 * words, from one place, and translated the moment a provider appears. It is not a licence
 * to skip the provider: an app renders inside one, and this is the seam for a component
 * that may also be rendered outside.
 *
 * Every app-level string still goes through `useT` and still throws without a provider.
 */
export function useOptionalT(): LocaleContextValue['t'] {
  const value = useContext(LocaleContext)
  if (value) return value.t
  return (key, fallback) => DESIGN_CATALOGUE_EN[key] ?? fallback ?? key
}
